import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import redditApi from '../../../services/redditApi';

const POLL_MS = 20_000;

const METHOD_LABELS = {
  paypal: 'PayPal',
  venmo: 'Venmo',
  wise: 'Wise',
  bank_transfer: 'Bank Transfer',
  upwork: 'Upwork',
  other: 'Other',
};

function periodLabel(period) {
  if (!period) return '';
  const parts = period.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const half = parts[2]; // 'H1' | 'H2' | undefined
  const monthLong = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  if (half === 'H1') {
    const monthShort = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short' });
    return `${monthShort} 1–15, ${y} (first half)`;
  }
  if (half === 'H2') {
    const monthShort = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short' });
    const last = new Date(y, m, 0).getDate();
    return `${monthShort} 16–${last}, ${y} (second half)`;
  }
  return monthLong;
}

const INVOICE_FROM_MONTH = '2026-08';

function invoiceAvailable(period, remaining) {
  const month = String(period || '').slice(0, 7);
  if (month >= INVOICE_FROM_MONTH) return true;
  return remaining != null && Number(remaining) > 0;
}

function StatusPill({ s }) {
  if (s === 'paid') return (
    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#dcfce7', color: '#166534' }}>Paid</span>
  );
  if (s === 'current') return (
    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#dbeafe', color: '#1e40af' }}>Current Period</span>
  );
  if (s === 'future') return (
    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#3730a3' }}>Upcoming</span>
  );
  return (
    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#ffedd5', color: '#9a3412' }}>Due</span>
  );
}

const KPI_ICONS = {
  earned: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  ),
  paid: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  due: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
    </svg>
  ),
  calendar: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  upwork: (
    <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
      <polyline points="7.5 4.21 12 6.81 16.5 4.21"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  ),
};

const SELECT_STYLE = {
  border: '1.5px solid var(--rh-border)',
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: 12,
  color: 'var(--rh-text)',
  background: '#fff',
  cursor: 'pointer',
  fontFamily: 'inherit',
  outline: 'none',
};

function KpiCard({ label, value, color, hint, icon }) {
  const colorMap = { default: 'default', green: 'green', orange: 'orange', blue: 'blue', purple: 'purple' };
  const cardClass = `rh-kpi-card rh-kpi-card--${colorMap[color] || 'default'}`;
  const iconClass = `rh-kpi-icon-wrap rh-kpi-icon--${colorMap[color] || 'default'}`;
  return (
    <div className={cardClass}>
      <div className="rh-kpi-body">
        <p className={`rh-kpi-value ${color || 'default'}`}>{value}</p>
        <p className="rh-kpi-label">{label}</p>
        {hint && <p className="rh-kpi-hint">{hint}</p>}
      </div>
      {icon && <div className={iconClass}>{KPI_ICONS[icon]}</div>}
    </div>
  );
}

/* ── Record Payment Modal ─────────────────────────────────────────── */
function RecordPaymentModal({ row, onClose, onRecorded, onViewInvoice }) {
  const [method, setMethod] = useState('paypal');
  const [note, setNote] = useState(`${periodLabel(row.period)} payout`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const upworkFee = method === 'upwork' ? (row.remaining * 0.05).toFixed(2) : null;

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await redditApi.post('/admin/payments/', {
        redditor_id: row.redditor_id,
        period: row.period,
        method,
        note,
      });
      onRecorded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to record payment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal">
        <h3 className="rh-modal-title">Record Payment</h3>
        <p className="rh-modal-subtitle">
          Paying <strong>{row.redditor_name}</strong> for {periodLabel(row.period)} at ${row.rate.toFixed(2)}/comment
        </p>

        <div className="rh-payments-modal-grid" style={{
          background: 'var(--rh-bg)', borderRadius: 10, padding: '14px 16px',
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px',
          marginBottom: 20, fontSize: 13,
        }}>
          <div>
            <p style={{ fontSize: 11, color: 'var(--rh-text-faint)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>
              {row.policy === 'live_only' ? 'Live Comments' : 'Submitted Comments'}
            </p>
            <p style={{ fontWeight: 700, fontSize: 17, margin: 0 }}>{row.submitted}</p>
            {onViewInvoice && (
              <button type="button" className="rh-link rh-invoice-inline-link" onClick={onViewInvoice}>
                View invoice
              </button>
            )}
          </div>
          <div>
            <p style={{ fontSize: 11, color: 'var(--rh-text-faint)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Amount Due</p>
            <p style={{ fontWeight: 700, fontSize: 17, color: 'var(--rh-orange)' }}>${row.remaining.toFixed(2)}</p>
          </div>
          {upworkFee && (
            <div style={{ gridColumn: '1 / -1' }}>
              <p style={{ fontSize: 11, color: '#6d28d9', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Upwork 5% Fee (accounting only)</p>
              <p style={{ fontWeight: 700, fontSize: 15, color: '#6d28d9' }}>${upworkFee}</p>
            </div>
          )}
        </div>

        {error && <div className="rh-auth-error" style={{ marginBottom: 12 }}>{error}</div>}

        <form onSubmit={onSubmit}>
          <div className="rh-form-group">
            <label>Payment Method</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              {Object.entries(METHOD_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="rh-form-group">
            <label>Note (optional)</label>
            <input
              type="text"
              placeholder="e.g. April batch, transaction ID"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="rh-modal-actions">
            <button type="button" className="rh-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="rh-btn-primary" disabled={busy}>
              {busy ? 'Recording…' : `Confirm Payment — $${row.remaining.toFixed(2)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Edit Payment Method Modal ───────────────────────────────────── */
function EditPaymentModal({ payment, onClose, onSaved }) {
  const [method, setMethod] = useState(payment.method);
  const [note, setNote] = useState(payment.note || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const upworkFee = method === 'upwork'
    ? (parseFloat(payment.amount) * 0.05).toFixed(2)
    : null;

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await redditApi.patch(`/admin/payments/${payment.id}/`, { method, note });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update payment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-modal-bg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rh-modal">
        <h3 className="rh-modal-title">Edit Payment</h3>
        <p className="rh-modal-subtitle">
          <strong>{payment.redditor_name}</strong> · {periodLabel(payment.period)} · ${parseFloat(payment.amount).toFixed(2)}
        </p>

        {upworkFee && (
          <div style={{
            background: '#f5f3ff', borderRadius: 10, padding: '10px 14px',
            marginBottom: 16, fontSize: 13, color: '#6d28d9',
          }}>
            <strong>Upwork 5% fee:</strong> ${upworkFee} — will be recorded for accounting purposes.
          </div>
        )}

        {error && <div className="rh-auth-error" style={{ marginBottom: 12 }}>{error}</div>}

        <form onSubmit={onSubmit}>
          <div className="rh-form-group">
            <label>Payment Method</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)}>
              {Object.entries(METHOD_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="rh-form-group">
            <label>Note</label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="rh-modal-actions">
            <button type="button" className="rh-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="rh-btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function invoiceNumber(invoice) {
  const tail = String(invoice.redditor_id || '').replace(/-/g, '').slice(-4).toUpperCase();
  return `INV-${invoice.period}-${tail}`;
}

function formatCommentDate(iso) {
  if (!iso) return '-';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

const INVOICE_ROWS_FIRST = 12;
const INVOICE_ROWS_NEXT = 16;

function paginateInvoiceComments(comments) {
  const list = comments || [];
  if (list.length === 0) return [[]];
  const pages = [list.slice(0, INVOICE_ROWS_FIRST)];
  for (let i = INVOICE_ROWS_FIRST; i < list.length; i += INVOICE_ROWS_NEXT) {
    pages.push(list.slice(i, i + INVOICE_ROWS_NEXT));
  }
  return pages;
}

function InvoiceItemsTable({ comments }) {
  return (
    <table className="rh-invoice-items">
      <colgroup>
        <col className="rh-invoice-col-sr" />
        <col className="rh-invoice-col-title" />
        <col className="rh-invoice-col-date" />
        <col className="rh-invoice-col-link" />
        <col className="rh-invoice-col-status" />
      </colgroup>
      <thead>
        <tr>
          <th>Sr</th>
          <th>Post title</th>
          <th>Commented on</th>
          <th>Link</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {comments.length === 0 ? (
          <tr>
            <td colSpan={5} className="rh-invoice-empty">No payable comments in this period.</td>
          </tr>
        ) : comments.map((c) => (
          <tr key={c.sr}>
            <td className="rh-invoice-num">{c.sr}</td>
            <td><span className="rh-invoice-title">{c.post_title || 'Untitled'}</span></td>
            <td className="rh-invoice-num">{formatCommentDate(c.submitted_date)}</td>
            <td>
              {c.submitted_url ? (
                <a className="rh-invoice-open" href={c.submitted_url} target="_blank" rel="noreferrer">
                  Open
                </a>
              ) : (
                <span className="rh-invoice-muted">-</span>
              )}
            </td>
            <td>
              <span className={`rh-invoice-status ${c.status === 'Live' ? 'is-live' : ''}`}>
                {c.status}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InvoiceTotals({ invoice, due }) {
  return (
    <footer className="rh-invoice-foot">
      <p className="rh-invoice-policy">
        Payable comments for this period
        {invoice.policy === 'live_only' ? '. Live comments only from June 2026.' : '.'}
      </p>
      <table className="rh-invoice-totals">
        <tbody>
          <tr>
            <th>Subtotal</th>
            <td>{money(invoice.amount)}</td>
          </tr>
          <tr>
            <th>Paid</th>
            <td>{money(invoice.paid)}</td>
          </tr>
          <tr className="rh-invoice-due-row">
            <th>Amount due</th>
            <td className={due ? 'is-due' : 'is-paid'}>{money(invoice.remaining)}</td>
          </tr>
        </tbody>
      </table>
    </footer>
  );
}

function printInvoice(invoice) {
  const due = Number(invoice.remaining) > 0;
  const pages = paginateInvoiceComments(invoice.comments);
  const totalPages = pages.length;
  const payLabel = due ? 'Payable to' : 'Paid to';

  const tableRows = (chunk) => {
    if (chunk.length === 0) {
      return `<tr><td colspan="5" class="empty">No payable comments in this period.</td></tr>`;
    }
    return chunk.map((c) => {
      const link = c.submitted_url
        ? `<a href="${escapeHtml(c.submitted_url)}">${escapeHtml(c.submitted_url)}</a>`
        : '<span class="muted">-</span>';
      return `<tr>
        <td class="num">${c.sr}</td>
        <td>${escapeHtml(c.post_title || 'Untitled')}</td>
        <td class="num">${escapeHtml(formatCommentDate(c.submitted_date))}</td>
        <td class="link">${link}</td>
        <td class="status"><span class="pill ${c.status === 'Live' ? 'live' : ''}">${escapeHtml(c.status)}</span></td>
      </tr>`;
    }).join('');
  };

  const sheets = pages.map((chunk, i) => {
    const page = i + 1;
    const isFirst = i === 0;
    const isLast = i === pages.length - 1;
    const header = isFirst
      ? `<header class="letterhead">
          <div>
            <p class="brand">Reddit Hub</p>
            <p class="brand-sub">Visibility Labs</p>
          </div>
          <p class="wordmark">Invoice</p>
        </header>
        <section class="parties">
          <div>
            <p class="label">${payLabel}</p>
            <p class="payee">${escapeHtml(invoice.redditor_name)}</p>
            <p class="meta-line">${invoice.comments_count} ${invoice.comments_count === 1 ? 'comment' : 'comments'} at ${money(invoice.rate)} each</p>
          </div>
          <dl class="meta">
            <div><dt>Invoice no.</dt><dd>${escapeHtml(invoiceNumber(invoice))}</dd></div>
            <div><dt>Period</dt><dd>${escapeHtml(periodLabel(invoice.period))}</dd></div>
            <div><dt>Status</dt><dd class="${due ? 'due' : 'paid'}">${due ? 'Amount due' : 'Paid in full'}</dd></div>
          </dl>
        </section>`
      : `<header class="cont">
          <p>Reddit Hub · Invoice · ${escapeHtml(invoice.redditor_name)} · ${escapeHtml(periodLabel(invoice.period))}</p>
          <p>Continued</p>
        </header>`;
    const foot = isLast
      ? `<footer class="foot">
          <p class="policy">Payable comments for this period${invoice.policy === 'live_only' ? '. Live comments only from June 2026.' : '.'}</p>
          <table class="totals">
            <tr><th>Subtotal</th><td>${money(invoice.amount)}</td></tr>
            <tr><th>Paid</th><td>${money(invoice.paid)}</td></tr>
            <tr class="due-row"><th>Amount due</th><td class="${due ? 'due' : 'paid'}">${money(invoice.remaining)}</td></tr>
          </table>
        </footer>`
      : '';
    return `<article class="sheet">
      ${header}
      <table class="items">
        <colgroup>
          <col class="sr" /><col class="title" /><col class="date" /><col class="link" /><col class="st" />
        </colgroup>
        <thead>
          <tr><th>Sr</th><th>Post title</th><th>Commented on</th><th>Link to comment</th><th>Status</th></tr>
        </thead>
        <tbody>${tableRows(chunk)}</tbody>
      </table>
      <div class="end">
        ${foot}
        <p class="page-num">Page ${page} of ${totalPages}</p>
      </div>
    </article>`;
  }).join('');

  const title = `Invoice - ${invoice.redditor_name} - ${periodLabel(invoice.period)}`;
  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>
    @page { size: A4 portrait; margin: 0; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: #cfc8c0;
      color: #2a241e;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .sheet {
      width: 210mm;
      height: 297mm;
      padding: 16mm;
      margin: 0 auto 12mm;
      background: #fffaf6;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .letterhead {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 12px;
      border-bottom: 2px solid #e03d00;
    }
    .brand { margin: 0; font-size: 12px; font-weight: 700; letter-spacing: 0.12em; text-transform: uppercase; }
    .brand-sub { margin: 4px 0 0; font-size: 11px; color: #6b6560; }
    .wordmark {
      margin: 0;
      font-family: Georgia, "Times New Roman", serif;
      font-size: 26px;
      font-weight: 700;
      letter-spacing: 0.16em;
      text-transform: uppercase;
      color: #e03d00;
      line-height: 1;
    }
    .cont {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 16px;
      padding-bottom: 10px;
      border-bottom: 1px solid #cfc8c0;
      font-size: 11px;
      color: #6b6560;
    }
    .cont p { margin: 0; }
    .parties {
      display: flex;
      justify-content: space-between;
      gap: 32px;
      padding: 16px 0 14px;
    }
    .label { margin: 0 0 4px; font-size: 9px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #6b6560; }
    .payee { margin: 0; font-size: 18px; font-weight: 700; }
    .meta-line { margin: 4px 0 0; font-size: 11px; color: #6b6560; }
    .meta { margin: 0; min-width: 230px; }
    .meta div { display: flex; justify-content: space-between; gap: 16px; margin: 0 0 7px; }
    .meta dt { font-size: 9px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #6b6560; }
    .meta dd { margin: 0; font-size: 11px; font-weight: 600; text-align: right; }
    .due { color: #e03d00; }
    .paid { color: #2f7a3e; }
    table.items { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 11px; }
    table.items th {
      text-align: left; font-size: 9px; font-weight: 700; letter-spacing: 0.08em;
      text-transform: uppercase; color: #6b6560; padding: 7px 8px 7px 0;
      border-bottom: 1px solid #cfc8c0;
    }
    table.items td {
      padding: 8px 8px 8px 0; vertical-align: top; border-bottom: 1px solid #ece7e1;
      line-height: 1.35; word-wrap: break-word;
    }
    table.items th:last-child, table.items td:last-child { padding-right: 0; text-align: right; }
    col.sr { width: 8%; }
    col.title { width: 38%; }
    col.date { width: 16%; }
    col.link { width: 26%; }
    col.st { width: 12%; }
    .num { font-variant-numeric: tabular-nums; white-space: nowrap; }
    .link a { color: #e03d00; text-decoration: none; word-break: break-all; font-size: 9px; }
    .muted { color: #9a948e; }
    .empty { text-align: center !important; color: #6b6560; padding: 18px 0 !important; }
    .pill {
      display: inline-block; padding: 1px 7px; border-radius: 999px;
      font-size: 9px; font-weight: 700; background: #eceff3; color: #4b5563;
    }
    .pill.live { background: #dcfce7; color: #166534; }
    .foot { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px; padding-top: 16px; }
    .policy { margin: 0; max-width: 48%; font-size: 10px; line-height: 1.45; color: #6b6560; }
    table.totals { width: 210px; border-collapse: collapse; font-variant-numeric: tabular-nums; }
    table.totals th { text-align: left; font-size: 11px; font-weight: 500; color: #6b6560; padding: 3px 16px 3px 0; }
    table.totals td { text-align: right; font-size: 11px; font-weight: 600; padding: 3px 0; }
    .due-row th, .due-row td {
      padding-top: 8px; border-top: 1.5px solid #2a241e; font-size: 12px; font-weight: 700;
    }
    .end { margin-top: auto; }
    .page-num {
      margin: 12px 0 0;
      text-align: center;
      font-size: 10px;
      color: #6b6560;
    }
    @media print {
      html, body { background: #fff; }
      .sheet {
        margin: 0;
        box-shadow: none;
        break-after: page;
        page-break-after: always;
      }
      .sheet:last-child {
        break-after: auto;
        page-break-after: auto;
      }
    }
  </style>
</head>
<body>
  ${sheets}
</body>
</html>`;

  const win = window.open('', 'reddit-invoice-print');
  if (!win) {
    window.alert('Allow popups in this browser to print the invoice.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  const run = () => {
    win.focus();
    win.print();
  };
  win.addEventListener('afterprint', () => {
    try { win.close(); } catch { /* already closed */ }
  });
  if (win.document.readyState === 'complete') {
    setTimeout(run, 50);
  } else {
    win.onload = () => setTimeout(run, 50);
  }
}

function InvoiceModal({ target, onClose }) {
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    redditApi.get('/admin/payments/invoice/', {
      params: { redditor_id: target.redditor_id, period: target.period },
    }).then(({ data }) => {
      if (!cancelled) setInvoice(data);
    }).catch((err) => {
      if (!cancelled) setError(err.response?.data?.detail || 'Failed to load invoice.');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [target.redditor_id, target.period]);

  const ready = Boolean(invoice);
  const due = ready ? Number(invoice.remaining) > 0 : true;
  const pages = ready ? paginateInvoiceComments(invoice.comments) : [];
  const totalPages = pages.length;

  return createPortal(
    <div className="rh-invoice-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rh-invoice-toolbar">
        <button type="button" className="rh-btn-secondary" onClick={onClose}>Close</button>
        <button
          type="button"
          className="rh-btn-primary"
          disabled={!ready}
          onClick={() => printInvoice(invoice)}
        >
          Print / PDF
        </button>
      </div>

      {!ready && (
        <article className="rh-invoice-sheet" aria-label="Invoice">
          <header className="rh-invoice-letterhead">
            <div>
              <p className="rh-invoice-brand">Reddit Hub</p>
              <p className="rh-invoice-brand-sub">Visibility Labs</p>
            </div>
            <p className="rh-invoice-wordmark">Invoice</p>
          </header>
          {loading && <p className="rh-invoice-loading">Preparing invoice…</p>}
          {error && <div className="rh-auth-error" style={{ marginBottom: 16 }}>{error}</div>}
        </article>
      )}

      {ready && (
        <div className="rh-invoice-stack">
          {pages.map((chunk, i) => {
            const page = i + 1;
            const isFirst = i === 0;
            const isLast = i === pages.length - 1;
            return (
              <article key={page} className="rh-invoice-sheet" aria-label={`Invoice page ${page} of ${totalPages}`}>
                {isFirst ? (
                  <>
                    <header className="rh-invoice-letterhead">
                      <div>
                        <p className="rh-invoice-brand">Reddit Hub</p>
                        <p className="rh-invoice-brand-sub">Visibility Labs</p>
                      </div>
                      <p className="rh-invoice-wordmark">Invoice</p>
                    </header>
                    <section className="rh-invoice-parties">
                      <div>
                        <p className="rh-invoice-meta-label">{due ? 'Payable to' : 'Paid to'}</p>
                        <p className="rh-invoice-payee">{invoice.redditor_name}</p>
                        <p className="rh-invoice-meta-line">
                          {invoice.comments_count} {invoice.comments_count === 1 ? 'comment' : 'comments'}
                          {' '}at {money(invoice.rate)} each
                        </p>
                      </div>
                      <dl className="rh-invoice-meta">
                        <div>
                          <dt>Invoice no.</dt>
                          <dd>{invoiceNumber(invoice)}</dd>
                        </div>
                        <div>
                          <dt>Period</dt>
                          <dd>{periodLabel(invoice.period)}</dd>
                        </div>
                        <div>
                          <dt>Status</dt>
                          <dd className={due ? 'is-due' : 'is-paid'}>{due ? 'Amount due' : 'Paid in full'}</dd>
                        </div>
                      </dl>
                    </section>
                  </>
                ) : (
                  <header className="rh-invoice-continued">
                    <p>Reddit Hub · Invoice · {invoice.redditor_name} · {periodLabel(invoice.period)}</p>
                    <p>Continued</p>
                  </header>
                )}

                <InvoiceItemsTable comments={chunk} />
                <div className="rh-invoice-sheet-end">
                  {isLast && <InvoiceTotals invoice={invoice} due={due} />}
                  <p className="rh-invoice-page-num">Page {page} of {totalPages}</p>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>,
    document.body,
  );
}

/* ── Main Tab ─────────────────────────────────────────────────────── */
export default function PaymentsTab() {
  const [billing, setBilling] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingRow, setPayingRow] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [invoiceTarget, setInvoiceTarget] = useState(null);
  const [toast, setToast] = useState('');

  // Monthly Breakdown filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [redditorFilter, setRedditorFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');

  // Payment History filters
  const [histRedditorFilter, setHistRedditorFilter] = useState('');
  const [histMethodFilter, setHistMethodFilter] = useState('');
  const [histDateFrom, setHistDateFrom] = useState('');
  const [histDateTo, setHistDateTo] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [bRes, hRes] = await Promise.all([
        redditApi.get('/admin/payments/billing/'),
        redditApi.get('/admin/payments/history/'),
      ]);
      setBilling(bRes.data);
      setHistory(hRes.data);
    } catch (err) {
      console.error('Failed to load payments', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, POLL_MS);
    return () => clearInterval(id);
  }, [fetchAll]);

  function onRecorded() {
    fetchAll();
    setToast('Payment recorded successfully.');
    setTimeout(() => setToast(''), 3500);
  }

  function onSaved() {
    fetchAll();
    setToast('Payment updated successfully.');
    setTimeout(() => setToast(''), 3500);
  }

  const kpis = billing?.kpis || {};
  const rows = billing?.rows || [];
  // Completed periods live in Payment History — this table is action items only.
  const actionRows = rows.filter((r) => r.status !== 'paid');

  // Unique redditor names for dropdowns
  const billingRedditors = [...new Set(actionRows.map((r) => r.redditor_name))].sort();
  const billingMonths = [...new Set(actionRows.map((r) => r.period))].sort().reverse();
  const histRedditors = [...new Set(history.map((p) => p.redditor_name))].sort();

  // Monthly Breakdown filtered rows
  const filteredRows = actionRows.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (redditorFilter && r.redditor_name !== redditorFilter) return false;
    if (monthFilter && r.period !== monthFilter) return false;
    return true;
  });

  // Payment History filtered rows
  const filteredHistory = history.filter((p) => {
    if (histRedditorFilter && p.redditor_name !== histRedditorFilter) return false;
    if (histMethodFilter && p.method !== histMethodFilter) return false;
    if (histDateFrom && p.paid_at < histDateFrom) return false;
    if (histDateTo && p.paid_at > histDateTo) return false;
    return true;
  });

  const dueCount = actionRows.filter((r) => r.status === 'due').length;
  const currentCount = actionRows.filter((r) => r.status === 'current').length;
  const futureCount = actionRows.filter((r) => r.status === 'future').length;

  const hasHistFilters = histRedditorFilter || histMethodFilter || histDateFrom || histDateTo;

  return (
    <>
      {toast && (
        <div className="rh-toast rh-toast-success">{toast}</div>
      )}
      <div className="rh-tab-scroll">

      <div className="rh-page-header">
        <h2 className="rh-page-title">Payments & Invoices</h2>
        <p className="rh-page-subtitle">
          Paid twice a month — days 1–15 and 16–end — for comments posted in each period (not the original due date)
        </p>
      </div>

      {/* Live-only payment policy notice */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        marginBottom: 16, padding: '12px 16px',
        background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
        fontSize: 13, color: '#1e40af', lineHeight: 1.6,
      }}>
        <svg width="16" height="16" fill="none" stroke="#1e40af" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: 1 }}>
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <span>
          <strong>Payment policy:</strong> From <strong>June 2026</strong>, redditors are paid only for
          comments that are currently <strong>live and visible</strong> on Reddit (deleted, instantly
          deleted, and archived) comments are not payable. From <strong>August 2026</strong>, payouts run
          twice a month (days <strong>1–15</strong> and <strong>16–end</strong>); earlier months stay
          monthly.
        </span>
      </div>

      {/* KPI Row — 4 cards */}
      <div className="rh-kpi-row rh-kpi-row-4">
        <KpiCard label="Total Paid" value={loading ? '—' : `$${kpis.total_paid?.toFixed(2) ?? '0.00'}`} color="green" icon="paid" />
        <KpiCard label="Due Now" value={loading ? '—' : `$${kpis.due_now?.toFixed(2) ?? '0.00'}`} color="orange" hint="Closed periods, unpaid" icon="due" />
        <KpiCard label="Current Period" value={loading ? '—' : `$${kpis.current_month?.toFixed(2) ?? '0.00'}`} color="blue" hint="Still accruing" icon="calendar" />
        <KpiCard label="Upwork Fees" value={loading ? '—' : `$${kpis.upwork_fees?.toFixed(2) ?? '0.00'}`} color="purple" hint="5% platform fees (accounting)" icon="upwork" />
      </div>

      {/* Monthly Breakdown */}
      <div className="rh-card">
        <div className="rh-card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 className="rh-card-title">Monthly Breakdown</h3>
            <p style={{ fontSize: 12, color: 'var(--rh-text-muted)', marginTop: 2 }}>
              Unpaid periods only. Completed payouts are in Payment History below.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Status filter pills */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { key: 'all', label: `All (${actionRows.length})` },
                { key: 'due', label: `Due (${dueCount})`, color: '#9a3412', bg: '#ffedd5' },
                { key: 'current', label: `Current Period (${currentCount})`, color: '#1e40af', bg: '#dbeafe' },
                ...(futureCount > 0 ? [{ key: 'future', label: `Upcoming (${futureCount})`, color: '#3730a3', bg: '#e0e7ff' }] : []),
              ].map(({ key, label, color, bg }) => (
                <button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  style={{
                    padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', border: 'none',
                    background: statusFilter === key ? (bg || 'var(--rh-orange-light)') : '#f3f4f6',
                    color: statusFilter === key ? (color || 'var(--rh-orange)') : 'var(--rh-text-muted)',
                    transition: 'all 0.15s',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Redditor filter */}
            <select value={redditorFilter} onChange={(e) => setRedditorFilter(e.target.value)} style={SELECT_STYLE}>
              <option value="">All Redditors</option>
              {billingRedditors.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>

            {/* Month filter */}
            <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} style={SELECT_STYLE}>
              <option value="">All Months</option>
              {billingMonths.map((m) => <option key={m} value={m}>{periodLabel(m)}</option>)}
            </select>

            {(redditorFilter || monthFilter) && (
              <button
                onClick={() => { setRedditorFilter(''); setMonthFilter(''); }}
                style={{ ...SELECT_STYLE, color: 'var(--rh-orange)', border: '1.5px solid var(--rh-orange)' }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="rh-table-wrap rh-table-scroll rh-payments-table-scroll">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Redditor</th>
                <th>Payable Comments</th>
                <th>Rate</th>
                <th>Earned</th>
                <th>Paid</th>
                <th>Remaining</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="rh-empty">Loading…</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={9} className="rh-empty">
                  {statusFilter === 'due' || statusFilter === 'all'
                    ? 'All caught up — no pending payouts.'
                    : 'No records match the selected filters.'}
                </td></tr>
              ) : filteredRows.map((r) => (
                <tr key={`${r.redditor_id}-${r.period}`} style={{ opacity: r.status === 'paid' ? 0.7 : 1 }}>
                  <td className="rh-font-medium">{periodLabel(r.period)}</td>
                  <td>{r.redditor_name}</td>
                  <td>
                    {invoiceAvailable(r.period, r.remaining) ? (
                      <button
                        type="button"
                        className="rh-invoice-count-btn"
                        onClick={() => setInvoiceTarget(r)}
                        title="View invoice"
                      >
                        {r.submitted}
                      </button>
                    ) : (
                      <span>{r.submitted}</span>
                    )}
                    {r.policy === 'live_only' && (
                      <span className="rh-live-only-tag">live only</span>
                    )}
                  </td>
                  <td className="rh-text-muted">${r.rate.toFixed(2)}</td>
                  <td className="rh-font-medium">${r.amount.toFixed(2)}</td>
                  <td style={{ color: '#16a34a', fontWeight: r.paid > 0 ? 600 : 400 }}>
                    {r.paid > 0 ? `$${r.paid.toFixed(2)}` : '—'}
                  </td>
                  <td style={{
                    fontWeight: 600,
                    color: r.remaining <= 0 ? 'var(--rh-text-muted)' : r.status === 'due' ? '#c2410c' : 'var(--rh-text)',
                  }}>
                    {r.remaining > 0 ? `$${r.remaining.toFixed(2)}` : <span style={{ color: '#16a34a' }}>Paid in full</span>}
                  </td>
                  <td><StatusPill s={r.status} /></td>
                  <td>
                    <div className="rh-payments-actions">
                      {invoiceAvailable(r.period, r.remaining) && (
                        <button
                          type="button"
                          className="rh-btn-secondary"
                          style={{ padding: '5px 12px', fontSize: 12 }}
                          onClick={() => setInvoiceTarget(r)}
                        >
                          Invoice
                        </button>
                      )}
                      {r.status === 'due' ? (
                        <button
                          className="rh-btn-primary"
                          style={{ padding: '5px 14px', fontSize: 12 }}
                          onClick={() => setPayingRow(r)}
                        >
                          Record Payment
                        </button>
                      ) : r.status === 'current' ? (
                        <span style={{ fontSize: 12, color: 'var(--rh-text-faint)' }}>Still accruing</span>
                      ) : r.status === 'future' ? (
                        <span style={{ fontSize: 12, color: 'var(--rh-text-faint)' }}>Upcoming month</span>
                      ) : (
                        <span style={{ fontSize: 12, color: '#16a34a' }}>Complete</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment History */}
      <div className="rh-card">
        <div className="rh-card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
          <h3 className="rh-card-title">Payment History</h3>

          {/* History filters */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={histRedditorFilter} onChange={(e) => setHistRedditorFilter(e.target.value)} style={SELECT_STYLE}>
              <option value="">All Redditors</option>
              {histRedditors.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>

            <select value={histMethodFilter} onChange={(e) => setHistMethodFilter(e.target.value)} style={SELECT_STYLE}>
              <option value="">All Methods</option>
              {Object.entries(METHOD_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>

            <input
              type="date"
              value={histDateFrom}
              onChange={(e) => setHistDateFrom(e.target.value)}
              style={{ ...SELECT_STYLE, width: 140 }}
              title="From date"
              placeholder="From"
            />
            <input
              type="date"
              value={histDateTo}
              onChange={(e) => setHistDateTo(e.target.value)}
              style={{ ...SELECT_STYLE, width: 140 }}
              title="To date"
            />

            {hasHistFilters && (
              <button
                onClick={() => { setHistRedditorFilter(''); setHistMethodFilter(''); setHistDateFrom(''); setHistDateTo(''); }}
                style={{ ...SELECT_STYLE, color: 'var(--rh-orange)', border: '1.5px solid var(--rh-orange)' }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="rh-table-wrap rh-table-scroll rh-payments-table-scroll">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Period</th>
                <th>Redditor</th>
                <th>Comments</th>
                <th>Amount</th>
                <th>Upwork Fee</th>
                <th>Method</th>
                <th>Note</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="rh-empty">Loading…</td></tr>
              ) : filteredHistory.length === 0 ? (
                <tr><td colSpan={9} className="rh-empty">
                  {hasHistFilters ? 'No payments match the selected filters.' : 'No payments recorded yet.'}
                </td></tr>
              ) : filteredHistory.map((p) => (
                <tr key={p.id}>
                  <td className="rh-text-sm rh-text-muted">{p.paid_at}</td>
                  <td className="rh-text-sm">{periodLabel(p.period)}</td>
                  <td className="rh-font-medium">{p.redditor_name}</td>
                  <td>{p.comments_count}</td>
                  <td style={{ color: '#16a34a', fontWeight: 600 }}>${parseFloat(p.amount).toFixed(2)}</td>
                  <td style={{ color: '#6d28d9', fontWeight: p.method === 'upwork' ? 600 : 400 }}>
                    {p.method === 'upwork' && parseFloat(p.upwork_fee) > 0
                      ? `$${parseFloat(p.upwork_fee).toFixed(2)}`
                      : <span style={{ color: 'var(--rh-text-faint)' }}>—</span>}
                  </td>
                  <td className="rh-text-sm">
                    {p.method === 'upwork' ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: '#f5f3ff', color: '#6d28d9',
                        padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                      }}>Upwork</span>
                    ) : (
                      <span style={{ color: 'var(--rh-text-muted)' }}>{METHOD_LABELS[p.method] || p.method}</span>
                    )}
                  </td>
                  <td className="rh-text-sm rh-text-muted">{p.note || '—'}</td>
                  <td>
                    <div className="rh-payments-actions">
                      {invoiceAvailable(p.period) && (
                        <button
                          type="button"
                          className="rh-btn-secondary"
                          style={{ padding: '5px 12px', fontSize: 12 }}
                          onClick={() => setInvoiceTarget({
                            redditor_id: p.redditor_id,
                            period: p.period,
                            redditor_name: p.redditor_name,
                          })}
                        >
                          Invoice
                        </button>
                      )}
                      <button
                        title="Edit payment method / note"
                        onClick={() => setEditingPayment(p)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: 'var(--rh-text-muted)', padding: '4px 6px',
                          borderRadius: 6, transition: 'color 0.15s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--rh-orange)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--rh-text-muted)'}
                      >
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {payingRow && (
        <RecordPaymentModal
          row={payingRow}
          onClose={() => setPayingRow(null)}
          onRecorded={onRecorded}
          onViewInvoice={invoiceAvailable(payingRow.period, payingRow.remaining)
            ? () => setInvoiceTarget(payingRow)
            : undefined}
        />
      )}

      {editingPayment && (
        <EditPaymentModal
          payment={editingPayment}
          onClose={() => setEditingPayment(null)}
          onSaved={onSaved}
        />
      )}

      {invoiceTarget && (
        <InvoiceModal
          target={invoiceTarget}
          onClose={() => setInvoiceTarget(null)}
        />
      )}

      </div>{/* end rh-tab-scroll */}
    </>
  );
}
